'use client'

import { css } from 'styled-components'

import { createBreakpoints } from './createBreakpoints'
import { Theme } from './types'
import { media } from './utils'

const SPACING_UNIT = 4

const breakpoints = createBreakpoints({
  tablet: 600,
  pc: 1024,
})

export const defaultTheme: Theme = {
  breakpoints,
  colors: {
    black: '#000',
    white: '#FFF',
    offWhite: '#F5F7F9',
    warning: '#D30A0A',
    gray: {
      '01': '#333',
      '02': '#767676',
      '03': '#949494',
      '04': '#CCC',
      '05': '#EEE',
      '06': '#FAFAFA',
    },
    red: {
      '01': '#FF0082',
      '02': '#FF7FC0',
      '03': '#FFD1E9',
    },
    blue: {
      '01': '#000064',
      '02': '#0091EA',
      '03': '#7FC8F4',
    },
    green: {
      '01': '#00C853',
    },
    yellow: {
      '01': '#FFB300',
    },
    link: '#0091FF',
    gradient: {
      primary:
        'linear-gradient(315deg, #FF8255 0%, #FF0082 63.01%, #C20098 100%)',
    },
  },
  radius: {
    xs: 2,
    sm: 4,
    base: 6,
    md: 8,
    lg: 12,
    xl: 16,
    full: 9999,
  },
  fontFamily: {
    base: [
      "var(--font-noto-sans-jp, 'Noto Sans JP')",
      'Helvetica Neue',
      'Helvetica',
      'Hiragino Sans',
      'Hiragino Kaku Gothic ProN',
      'Arial',
      'Yu Gothic',
      'Meiryo',
      'sans-serif',
    ].join(', '),
  },
  typography: {
    catch: css`
      font-weight: 500;
      letter-spacing: 1px;
      font-size: 34px;
      line-height: 50px;

      ${media.pc} {
        font-size: 40px;
        line-height: 59px;
      }
    `,
    headline1: css`
      font-weight: 500;
      letter-spacing: 1px;
      font-size: 24px;
      line-height: 36px;

      ${media.pc} {
        font-size: 30px;
        line-height: 44px;
      }
    `,
    headline2: css`
      font-weight: 500;
      font-size: 22px;
      line-height: 32px;
      letter-spacing: 1px;

      ${media.pc} {
        font-size: 26px;
        line-height: 40px;
      }
    `,
    headline3: css`
      font-weight: 500;
      font-size: 18px;
      line-height: 30px;
      letter-spacing: 1px;
    `,
    subhead1: css`
      font-weight: 500;
      font-size: 16px;
      line-height: 26px;
      letter-spacing: 1px;
    `,
    subhead2: css`
      font-weight: 500;
      font-size: 14px;
      line-height: 22px;
      letter-spacing: 1px;
    `,
    subhead3: css`
      font-weight: 500;
      font-size: 12px;
      line-height: 18px;
      letter-spacing: 1px;
    `,
    body1: css`
      font-weight: 300;
      font-size: 16px;
      line-height: 26px;
      letter-spacing: 1px;
    `,
    body2: css`
      font-weight: 300;
      font-size: 14px;
      line-height: 24px;
      letter-spacing: 1px;
    `,
    caption1: css`
      font-weight: 300;
      font-size: 12px;
      line-height: 18px;
      letter-spacing: 1px;
    `,
    caption2: css`
      font-weight: 300;
      font-size: 10px;
      line-height: 15px;
      letter-spacing: 1px;
    `,
    button1: css`
      font-weight: 400;
      font-size: 18px;
      line-height: 24px;
      letter-spacing: 1px;
    `,
    button2: css`
      font-weight: 400;
      font-size: 16px;
      line-height: 22px;
      letter-spacing: 1px;
    `,
    button3: css`
      font-weight: 400;
      font-size: 14px;
      line-height: 18px;
      letter-spacing: 1px;
    `,
  },
  spacing: (...args: number[]) => {
    switch (args.length) {
      case 1:
        return `${(args[0] as number) * SPACING_UNIT}px`
      case 2:
      case 3:
      case 4:
        return args.map((v) => `${v * SPACING_UNIT}px`).join(' ')
      default:
        throw new Error('Spacing supports arguments up to length of 4')
    }
  },
  // For styled-system
  space: Array.from({ length: 30 + 1 }, (_, i) => i * SPACING_UNIT),
}

export type DefaultTheme = typeof defaultTheme
